import Vue from 'vue'
import Vuex from 'vuex'
import { login, logout, refreshTokens } from '@/api/Auth/auth.api'
import {
  LoginInterface,
  TokensInterface,
  User,
} from '@/api/Auth/auth.interface'
import { AxiosResponse } from 'axios'
import router from '@/router'
import { getAgencyById } from '@/api/Agency/agency.api'
import { Agency } from '@/api/Agency/agency.interface'
import * as remittance from './modules/remittance'
import * as notifications from './modules/notifications'
import * as remittanceChat from './modules/remittanceChat'
import * as alert from './modules/alert'
import { getProfile } from '@/api/Auth/auth.api'
import LogRocket from 'logrocket'

// eslint-disable-next-line @typescript-eslint/ban-ts-ignore
//@ts-ignore
import createPlugin from 'logrocket-vuex'

const logrocketPlugin = createPlugin(LogRocket, function(mutation: any) {
  if (mutation.type === 'UPDATE_ACCESS') {
    return null
  }
  return mutation
})

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    accessToken: 'no-token',
    barColor: 'rgba(255, 255, 255, 0.90), rgba(255, 255, 255, 0.90)',
    miniVariant: null as null | boolean,
    profile: null as null | User,
    pendingAuth: null as Promise<AxiosResponse<TokensInterface>> | null,
    agency: null as null | Agency,
    loading: false,
    initialized: false,
  },
  mutations: {
    TOGGLE_LOADING: (state, payload: boolean) => (state.loading = payload),
    TOGGLE_TOOLBAR: (state, payload: boolean) => (state.miniVariant = payload),
    UPDATE_ACCESS: (state, payload: TokensInterface): void => {
      state.accessToken = payload.access
    },
    UPDATE_PROFILE: (state, user: User): void => {
      state.profile = user
    },
    UPDATE_AGENCY: (state, agency: Agency): void => {
      state.agency = agency
    },
    PENDING_AUTH: (
      state,
      payload: Promise<AxiosResponse<TokensInterface>> | null,
    ) => {
      state.pendingAuth = payload
    },
    loading: (state, payload) => (state.loading = payload),
  },
  getters: {
    isLoggedIn: (state): boolean => !!state.accessToken,
    pendingAuth: (state): Promise<AxiosResponse<TokensInterface>> | null =>
      state.pendingAuth,
    agency: (state): Agency | null => state.agency,
    profile: (state): User | null => state.profile,
    loading: (state): boolean => state.loading,
  },
  actions: {
    login: async (
      context,
      payload: LoginInterface,
    ): Promise<boolean | AxiosResponse<TokensInterface>> => {
      try {
        const response = await login(payload)
        context.commit('UPDATE_ACCESS', response.data)

        return response
      } catch (error) {
        return false
      }
    },
    refreshToken: async (context): Promise<string | void> => {
      try {
        const pendingAuth = context.getters.pendingAuth
        if (pendingAuth) {
          const response = await pendingAuth
          context.commit('PENDING_AUTH', null)
          return response.data.access
        } else {
          const request = refreshTokens()
          context.commit('PENDING_AUTH', request)
          const response = await request
          if (response.status === 200) {
            context.commit('UPDATE_ACCESS', response.data)
            context.commit('PENDING_AUTH', null)
            return response.data.access
          } else context.commit('PENDING_AUTH', null)
        }
      } catch {}
    },
    LOGOUT_USER: async (context): Promise<void> => {
      try {
        await logout()
      } catch (error) {
        console.log(error)
      } finally {
        if (router.currentRoute.name !== 'login') {
          await router.push({ name: 'login' })
        }
        context.state.accessToken = ''
        context.state.profile = null
        context.state.agency = null
      }
    },
    getProfileInfo: async (context): Promise<void> => {
      const user = await getProfile()
      context.commit('UPDATE_PROFILE', user.data)
      if (user.data.role === 'ADMIN') {
        const agency = await getAgencyById(user.data.agency)
        context.commit('UPDATE_AGENCY', agency.data)
      } else {
        context.commit('UPDATE_AGENCY', {
          uuid: user.data.agency,
          name: user.data.agency_name,
          image: user.data.agency_image,
        })
      }
    },
  },
  modules: { remittance, notifications, remittanceChat, alert },
  plugins: [logrocketPlugin],
})
